import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { celebrationQueue } from './celebrationQueue'
import type { CelebrationItem } from './celebrationQueue'
import { ConfettiLayer } from './ConfettiLayer'
import { useSettingsStore } from '@/state/useSettingsStore'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useTenant } from '@/tenant/TenantProvider'
import { useViewStore } from '@/state/useViewStore'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { computeEvolution } from '@/milestones/evolution'
import { RankMedallion } from '@/components/trophies/RankMedallion'
import { getRingTone } from '@/components/trophies/RankRing'
import type { EvolutionTier } from '@/milestones/types'
import { motifForTier, playMotif } from '@/lib/soundManager'
import { useTranslation } from 'react-i18next'
import { describeMilestone } from '@/milestones/labels'
import { Sparkles } from 'lucide-react'
import { appBus } from '@/lib/eventBus'

const MIN_DURATION_MS = 4500
const TIER_BONUS_PER_LEVEL_MS = 700

export function CelebrationOverlay() {
  const { t } = useTranslation()
  const [active, setActive] = useState<CelebrationItem | null>(null)
  const locale = useSettingsStore((s) => s.locale)
  const tenant = useTenant()
  const pause = useViewStore((s) => s.pause)
  const resume = useViewStore((s) => s.resume)
  const defs = useMemo(
    () =>
      (tenant.milestones.defaultsEnabled ? DEFAULT_MILESTONES : []).concat(
        tenant.milestones.customDefinitions ?? [],
      ),
    [tenant],
  )

  useEffect(() => {
    return appBus.on('trophy-unlocked', (trophy) => {
      const def = defs.find((d) => d.id === trophy.milestoneId)
      if (!def) return
      const tiers = computeEvolution(useDashboardStore.getState().snapshot)
      celebrationQueue.push({
        id: trophy.milestoneId,
        trophy,
        def,
        newTier: tiers[def.category],
      })
    })
  }, [defs])

  useEffect(() => {
    return celebrationQueue.subscribe((items) => {
      if (!active && items.length > 0) {
        const top = celebrationQueue.pop()
        if (top) {
          setActive(top)
          pause('celebration')
          appBus.emit('celebration:start', { id: top.id })
          const tier = top.newTier ?? 1
          playMotif(motifForTier(tier))
          const duration = MIN_DURATION_MS + tier * TIER_BONUS_PER_LEVEL_MS
          window.setTimeout(() => {
            appBus.emit('celebration:end', { id: top.id })
            setActive(null)
            resume()
          }, duration)
        }
      }
    })
  }, [active, pause, resume])

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key={active.id}
          className="pointer-events-none fixed inset-0 z-40 grid place-items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <ConfettiLayer active intensity={Math.min(1, 0.4 + (active.newTier ?? 0) * 0.15)} />
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <motion.div
            className="pointer-events-auto relative z-10 flex max-w-2xl flex-col items-center gap-4 rounded-3xl border border-brand-primary/40 bg-surface-1/95 px-10 py-8 text-center shadow-2xl"
            initial={{ scale: 0.85, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: -10 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-primary/40 bg-brand-primary/10 px-3 py-1 text-xs uppercase tracking-widest text-brand-primary">
              <Sparkles className="size-3.5" />
              {t('celebrations.milestone_unlocked')}
            </div>
            <RankMedallion
              category={active.def.category}
              tier={(active.newTier ?? 1) as EvolutionTier}
              size={220}
            />
            <TierBanner tier={(active.newTier ?? 1) as EvolutionTier} locale={locale} />
            <h2 className="font-display text-4xl font-bold tracking-tight text-ink">
              {describeMilestone(active.def, locale).title}
            </h2>
            <p className="max-w-md text-lg text-ink-muted">
              {describeMilestone(active.def, locale).flavor}
            </p>
            <div className="mt-2 text-xs uppercase tracking-widest text-ink-subtle">
              {t(`milestones.category.${active.def.category}`)}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const TIER_NAMES_CZ: Record<EvolutionTier, string> = {
  0: 'Železo',
  1: 'Bronz',
  2: 'Stříbro',
  3: 'Zlato',
  4: 'Platina',
  5: 'Diamant',
}
const TIER_NAMES_EN: Record<EvolutionTier, string> = {
  0: 'Iron',
  1: 'Bronze',
  2: 'Silver',
  3: 'Gold',
  4: 'Platinum',
  5: 'Diamond',
}

function TierBanner({ tier, locale }: { tier: EvolutionTier; locale: 'cs' | 'en' }) {
  const tone = getRingTone(tier)
  const name = (locale === 'cs' ? TIER_NAMES_CZ : TIER_NAMES_EN)[tier]
  return (
    <div className="w-80">
      <svg viewBox="0 0 280 50" className="w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="celebration-banner-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={tone.highlight} />
            <stop offset="50%" stopColor={tone.outer} />
            <stop offset="100%" stopColor={tone.shadow} />
          </linearGradient>
        </defs>
        <path
          d="M 6 8 L 24 0 L 256 0 L 274 8 L 256 50 L 24 50 Z"
          fill="url(#celebration-banner-grad)"
          stroke={tone.highlight}
          strokeOpacity="0.4"
          strokeWidth="0.8"
        />
        <text
          x="140"
          y="33"
          textAnchor="middle"
          fontSize="22"
          fontWeight="800"
          fontFamily="Inter Tight"
          letterSpacing="5"
          fill={tier <= 1 ? '#f0f0f0' : '#0a0a18'}
        >
          {name.toUpperCase()}
        </text>
      </svg>
    </div>
  )
}
